import React, { useEffect, useState } from 'react';
import moment from 'moment';

import Modal from '../../../../../shared/components/core-ui/modal/modal.component';
import Button, {
  ButtonSize,
  ButtonType
} from '../../../../../shared/components/core-ui/button/button.component';
import useWindowResizer from '../../../../../shared/hooks/window-resizer/window-resizer.hook';
import { birdToast } from '../../../../../shared/components/core-ui/toast';
import { CURRENCY } from '../../../../../shared/enums/currency.enum';
import { GuestEmailType } from '../../../../../shared/enums/guest-email-type.enum';
import { PaymentStatus } from '../../../../../shared/enums/payment-status.enum';
import { ReservationStatus } from '../../../../../shared/enums/reservation-status';
import { PaymentModel } from '../../../../../shared/models/payment.model';
import { ReservationDetailsUiModel } from '../../../../../shared/ui-models/reservation.ui-model';
import { isErrorInfo } from '../../../../../shared/interfaces/error-info.interface';
import { resendGuestEmail } from '../../../../shared/services/data.service';

export interface ResendGuestEmailModalProps {
  config: {
    showModal: boolean;
    /**
     * When set, the modal is locked to resending the confirmation for this one payment —
     * used by the per-payment action in the payments table.
     */
    lockedPayment?: PaymentModel;
  };
  data: {
    reservation: ReservationDetailsUiModel;
    guestEmail: string;
  };
  eventHandlers: {
    closeHandler: () => void;
    onSent?: () => void;
  };
}

const EMAIL_OPTIONS: { type: GuestEmailType; label: string; description: string }[] = [
  {
    type: GuestEmailType.CHECK_IN_CONFIRMATION,
    label: 'Check-in Confirmation',
    description: 'Confirms the guest is checked in and carries the unit onboarding message.'
  },
  {
    type: GuestEmailType.PAYMENT_CONFIRMATION,
    label: 'Payment Confirmation',
    description: 'Confirms a collected payment and lists its breakdown.'
  }
];

/** Statuses where a check-in confirmation actually makes sense to (re)send. */
const CHECKED_IN_STATUSES: string[] = [
  ReservationStatus.CHECKED_IN,
  ReservationStatus.IN_HOUSE,
  ReservationStatus.CHECKED_OUT
];

function getCurrencyPrefix(currency?: string): string {
  return currency ? `${CURRENCY[currency as keyof typeof CURRENCY]} ` : 'EGP ';
}

/** Only a payment the guest actually paid has a confirmation worth resending. */
function isConfirmablePayment(payment: PaymentModel): boolean {
  return (
    payment.direction !== 'out' &&
    [PaymentStatus.COLLECTED, PaymentStatus.PARTIALLY_COLLECTED].indexOf(payment.status) > -1
  );
}

function describePayment(payment: PaymentModel): string {
  const paidOn = payment.collectedAt || payment.dueDate;
  const amount = `${getCurrencyPrefix(payment.currency)}${Number(
    payment.paymentAmount || 0
  ).toLocaleString()}`;

  return `#${payment.id} · ${amount}${paidOn ? ` · ${moment(paidOn).format('D MMM, YYYY')}` : ''}`;
}

/**
 * functional component ResendGuestEmailModal
 *
 * Lets the CX team re-trigger a guest facing email when the original was never delivered
 * and the guest asks for it again. The email is always sent to the guest's address on the
 * booking, and every send is written to the reservation history by the backend.
 */
export const ResendGuestEmailModal: React.FC<ResendGuestEmailModalProps> = ({
  config: { showModal, lockedPayment },
  data: { reservation, guestEmail },
  eventHandlers: { closeHandler, onSent }
}) => {
  const isMobileView = useWindowResizer();

  const [emailType, setEmailType] = useState<GuestEmailType>(GuestEmailType.CHECK_IN_CONFIRMATION);
  const [paymentId, setPaymentId] = useState<string>('');
  const [isSending, setIsSending] = useState<boolean>(false);

  const confirmablePayments = (reservation.paymentList || []).filter(isConfirmablePayment);

  // A check-in confirmation is only sendable once the guest is actually checked in, the same
  // way a payment confirmation is only sendable for a collected payment. Both are enforced by
  // admin-connect too; this just keeps CX from attempting a send that will be rejected.
  const canSendCheckIn = CHECKED_IN_STATUSES.indexOf(reservation.status) > -1;
  const canSendPaymentConfirmation = confirmablePayments.length > 0;
  const isEmailTypeAvailable = (type: GuestEmailType) =>
    type === GuestEmailType.CHECK_IN_CONFIRMATION ? canSendCheckIn : canSendPaymentConfirmation;
  const hasAnySendableEmail = canSendCheckIn || canSendPaymentConfirmation;

  // Reopening the modal should always start from a clean, context-appropriate selection.
  useEffect(() => {
    if (!showModal) return;

    if (lockedPayment) {
      setEmailType(GuestEmailType.PAYMENT_CONFIRMATION);
      setPaymentId(String(lockedPayment.id));
    } else {
      setEmailType(
        canSendCheckIn ? GuestEmailType.CHECK_IN_CONFIRMATION : GuestEmailType.PAYMENT_CONFIRMATION
      );
      setPaymentId(confirmablePayments.length === 1 ? String(confirmablePayments[0].id) : '');
    }
  }, [showModal, lockedPayment]);

  const isPaymentConfirmation = emailType === GuestEmailType.PAYMENT_CONFIRMATION;
  const hasGuestEmail = !!guestEmail;
  const isValid =
    hasGuestEmail &&
    isEmailTypeAvailable(emailType) &&
    (!isPaymentConfirmation || !!paymentId) &&
    !isSending;

  const handleSend = () => {
    if (!isValid) return;

    setIsSending(true);
    resendGuestEmail(
      String(reservation.id),
      emailType,
      isPaymentConfirmation ? paymentId : undefined
    )
      .then((response) => {
        if (isErrorInfo(response)) {
          birdToast.error('Failed to send the email, please try again or contact support.');
          return;
        }

        birdToast.success(`Email sent to ${response.email}`);
        closeHandler();
        if (onSent) onSent();
      })
      .finally(() => setIsSending(false));
  };

  return (
    <Modal
      config={{ showModal, fullScreen: false, showClose: true, maxWidth: 'sm', autoHeight: true }}
      eventHandlers={{ closeModalHandler: closeHandler }}
    >
      <div className="flex flex-col gap-4 p-6">
        <div className="flex flex-col gap-1">
          <h2 className="m-0 text-2xl font-bold text-gray-800 font-gilroy-medium">
            Resend Guest Email
          </h2>
          <p className="m-0 text-xl text-gray-500 font-gilroy-medium">
            Re-trigger an email the guest never received.
          </p>
        </div>

        {/* Recipient */}
        <div className="rounded-lg bg-gray-50 border border-gray-200 px-3 py-2">
          <div className="text-lg uppercase text-gray-400 font-gilroy-medium">Sending to</div>
          <div className="text-xl text-gray-800 font-gilroy-medium break-all">
            {guestEmail || 'No guest email on this reservation'}
          </div>
        </div>

        {/* Email type */}
        <div className="flex flex-col gap-2">
          {EMAIL_OPTIONS.map((option) => {
            const isSelected = emailType === option.type;
            const isUnavailable = !isEmailTypeAvailable(option.type);
            const isDisabled =
              isUnavailable ||
              (!!lockedPayment && option.type !== GuestEmailType.PAYMENT_CONFIRMATION);
            const unavailableReason =
              option.type === GuestEmailType.CHECK_IN_CONFIRMATION
                ? 'Only available once the guest is checked in.'
                : 'No collected payment on this reservation.';

            return (
              <button
                key={option.type}
                type="button"
                disabled={isDisabled}
                onClick={() => setEmailType(option.type)}
                className={`flex flex-col gap-0.5 rounded-lg border px-3 py-2.5 text-left transition-colors ${
                  isSelected
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-gray-200 bg-white hover:border-primary-200 hover:bg-primary-50/40'
                } ${isDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
              >
                <span
                  className={`text-xl font-gilroy-medium font-semibold ${
                    isSelected ? 'text-primary-700' : 'text-gray-700'
                  }`}
                >
                  {option.label}
                </span>
                <span
                  className={`text-lg font-gilroy-medium ${
                    isUnavailable ? 'text-orange-600' : 'text-gray-500'
                  }`}
                >
                  {isUnavailable ? unavailableReason : option.description}
                </span>
              </button>
            );
          })}
        </div>

        {!hasAnySendableEmail && (
          <div className="rounded-lg border border-orange-200 bg-orange-50 px-3 py-2 text-lg text-orange-700 font-gilroy-medium">
            Neither email can be sent for this reservation yet.
          </div>
        )}

        {/* Payment picker */}
        {isPaymentConfirmation && (
          <div className="flex flex-col gap-1">
            <label className="text-lg uppercase text-gray-400 font-gilroy-medium">Payment</label>
            {confirmablePayments.length === 0 ? (
              <div className="rounded-lg border border-orange-200 bg-orange-50 px-3 py-2 text-lg text-orange-700 font-gilroy-medium">
                This reservation has no collected payment to confirm.
              </div>
            ) : (
              <select
                value={paymentId}
                disabled={!!lockedPayment}
                onChange={(event) => setPaymentId(event.target.value)}
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-xl text-gray-800 font-gilroy-medium disabled:bg-gray-50 disabled:text-gray-500"
              >
                <option value="">Select a payment</option>
                {confirmablePayments.map((payment) => (
                  <option key={payment.id} value={payment.id}>
                    {describePayment(payment)}
                  </option>
                ))}
              </select>
            )}
          </div>
        )}

        <div className="flex flex-col md:flex-row justify-end gap-2 pt-1">
          <Button
            onClick={closeHandler}
            type={ButtonType.primaryInverted}
            tracking={{ event: 'record_edit_cancelled', entity: 'reservation', method: 'close' }}
            size={isMobileView ? ButtonSize.full : ButtonSize.medium}
          >
            Cancel
          </Button>
          <Button
            loading={isSending}
            disabled={!isValid}
            onClick={handleSend}
            tracking={{
              event: 'record_saved',
              entity: 'reservation',
              action: 'update',
              outcome: 'success'
            }}
            size={isMobileView ? ButtonSize.full : ButtonSize.medium}
          >
            {isSending ? 'Sending...' : 'Send Email'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
